import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast, FileDown, FilterX, ListFilter } from 'lucide-react';
import ActionMenu from '../../components/common/ActionMenu';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import DownloadModal from '../../components/common/DownloadModal';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import { dataTransferApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { getRegenerateTarget, regenerateState } from '../../utils/exportRecipe';
import { canonicalQuery, hasColumnFilters, serializeFiltersForServer, SEARCH_HINT } from '../../utils/listSearch';
import StuiSelect from '../../components/common/StuiSelect';
import StuiDateTime from '../../components/common/StuiDateTime';
import { formatTimestamp, formatDateOnly, localDaysToUtcRange } from '../../utils/dateTime';

/**
 * Data Transfer History — every time data left the app (or came in through an import), read as a
 * monitor for scraping rather than one person's download list. Rows are AudTrl_DataTransfer.
 *
 * Scope follows the caller (DataTransferController):
 *   My Transfers   — your own, every database
 *   This Database  — everyone's on the current database; AppAdmin on it, or SysAdmin
 *   All Databases  — everyone, everywhere; SysAdmin
 * The page offers only the scopes /scopes says the API will serve.
 *
 * Summary groups transfers per user, per database, per day: one person pulling far more than
 * usual stands out there, where the flat list buries it.
 *
 * Selecting a row (click; click again clears) offers Regenerate Report in the action menu when the
 * row is a completed report download whose recipe can be rebuilt; double-click does it directly. It
 * re-runs the report with the saved filters and render options under the viewer's own permissions.
 *
 * Paging, search, header filters and sort all run on the server — the table spans every user, so
 * it is never loaded whole — and the header filter lists come from the whole filtered set.
 */

// What search reads (DataTransferController keys), labelled for field: prefixes and the Advanced
// Search builder. Requests carry the canonical, key-based form.
const SEARCH_FIELD_LABELS = [
  { key: 'name', label: 'Name' }, { key: 'userName', label: 'User' },
  { key: 'databaseSlug', label: 'Database' }, { key: 'format', label: 'Format' },
  { key: 'type', label: 'Type' }, { key: 'status', label: 'Status' },
  { key: 'machineName', label: 'Machine' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

const SCOPE_LABELS = { mine: 'My Transfers', database: 'This Database', all: 'All Databases' };

const DEFAULT_PAGE_SIZE = 50;
const NO_SORT = { key: null, direction: null };

// Keys match DataTransferController's filter and sort whitelists. File Name and Parameters live in
// the export recipe JSON, so the server cannot filter or sort them.
const GRID_COLUMNS = [
  { key: 'createdAt',       label: 'When',       width: 160, filterType: 'date' },
  { key: 'userName',        label: 'User',       width: 150 },
  { key: 'databaseSlug',    label: 'Database',   width: 100 },
  { key: 'name',            label: 'Name',       width: 260,
    renderCell: (row) => <span className="stui-fw-semibold">{row.name}</span> },
  { key: 'format',          label: 'Format',     width: 80,
    renderCell: (row) => <span className="stui-text-italic">{row.format?.toUpperCase() || '—'}</span> },
  { key: 'type',            label: 'Type',       width: 90,
    renderCell: (row) => <span className="stui-text-capitalize">{row.type}</span> },
  { key: 'status',          label: 'Status',     width: 90,
    renderCell: (row) => <span className={`stui-status-${row.status}`}>{row.status}</span> },
  { key: 'rowsTransferred', label: 'Rows',       width: 80, filterType: 'number' },
  { key: 'fileName',        label: 'File Name',  width: 200, filterable: false },
  { key: 'parameters',      label: 'Parameters', width: 240, filterable: false },
  { key: 'machineName',     label: 'Machine',    width: 120 },
];

const DOWNLOAD_COLUMNS = [
  { key: 'when',            label: 'When',      width: 160 },
  { key: 'userName',        label: 'User',      width: 160 },
  { key: 'databaseSlug',    label: 'Database',  width: 100 },
  { key: 'name',            label: 'Name',      width: 260 },
  { key: 'format',          label: 'Format',    width: 70  },
  { key: 'type',            label: 'Type',      width: 80  },
  { key: 'status',          label: 'Status',    width: 80  },
  { key: 'rowsTransferred', label: 'Rows',      width: 70  },
  { key: 'fileName',        label: 'File Name', width: 220 },
  { key: 'parameters',      label: 'Parameters', width: 260 },
  { key: 'machineName',     label: 'Machine',   width: 120 },
];

const parseRecipe = (exportRecipe) => {
  try { return exportRecipe ? JSON.parse(exportRecipe) : null; }
  catch { return null; }
};

// A report's committed filters, as "Field: a, b". "All" selections say nothing and are dropped.
const describeParams = (recipe) => {
  const params = recipe?.filterParams;
  if (!params) return '';
  return Object.entries(params)
    .map(([field, sels]) => {
      const picked = (Array.isArray(sels) ? sels : [sels])
        .filter(s => s && s.id !== '__all__' && s.value !== '__all__')
        .map(s => (typeof s === 'object' ? (s.display ?? s.value) : String(s)));
      return picked.length ? `${field}: ${picked.join(', ')}` : null;
    })
    .filter(Boolean)
    .join('; ');
};

// Why Regenerate Report is unavailable for the selected row, or null when it is available.
const regenerateBlocker = (row) => {
  if (!row) return 'Select a transfer to regenerate it';
  if (row.type !== 'download') return 'Only downloads can be regenerated';
  if (row.status !== 'complete') return 'Only completed downloads can be regenerated';
  if (!getRegenerateTarget(row.exportRecipe)) return 'This export did not save a report recipe that can be rebuilt';
  return null;
};

const formatWhen = (iso) => formatTimestamp(iso) || '—';

const count = (n) => (n == null ? '—' : n.toLocaleString());

// The server row as the grid shows it: NULLs read as '—', which is also the token that filters to
// NULL, and the recipe's file name and filters pulled out.
const toGridRow = (r) => {
  const recipe = parseRecipe(r.exportRecipe);
  return {
    ...r,
    createdAt:       formatWhen(r.createdAt),
    userName:        r.userName || '—',
    databaseSlug:    r.databaseSlug || '—',
    machineName:     r.machineName || '—',
    rowsTransferred: r.rowsTransferred ?? '—',
    fileName:        recipe?.fileName || '—',
    parameters:      describeParams(recipe) || '—',
  };
};

const DataTransferHistoryPage = ({ adminView = false }) => {
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const [scopes, setScopes]   = useState(null);   // null until /scopes answers
  const [scope, setScope]     = useState(null);
  const [view, setView]       = useState('transfers');

  // Same date filter as Audit Trail - Admin: dates are UTC days, and "To" includes its whole day.
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [appliedFrom, setAppliedFrom]       = useState('');
  const [appliedTo, setAppliedTo]           = useState('');
  const dateFilterRef                       = useRef(null);
  const dateFilterActive                    = !!(appliedFrom || appliedTo);

  const [rows, setRows]             = useState([]);
  const [groups, setGroups]         = useState([]);
  const [summaryTruncated, setSummaryTruncated] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading]       = useState(false);

  const [filters, setFilters]       = useState({});
  const [sortConfig, setSortConfig] = useState(null);   // null = newest first (server default)

  const [searchTerm, setSearchTerm]       = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showAdvSearch, setShowAdvSearch] = useState(false);

  // Single-row selection, by id. The row itself is read from the current page, so a refresh keeps
  // the selection while the row is still listed and drops it once it is not.
  const [selectedId, setSelectedId] = useState(null);

  const [showDownload, setShowDownload]           = useState(false);
  const [downloadRows, setDownloadRows]           = useState([]);
  const [preparingDownload, setPreparingDownload] = useState(false);

  // Which views this caller gets. The admin page opens on everything; the header's way in opens
  // on the widest view the caller holds for this database, falling back to their own.
  useEffect(() => {
    dataTransferApi.getScopes()
      .then(res => {
        const allowed = res.data?.scopes || [];
        setScopes(allowed);
        const preferred = adminView ? ['all', 'database', 'mine'] : ['database', 'mine', 'all'];
        setScope(preferred.find(s => allowed.includes(s)) || null);
      })
      .catch(() => { setScopes([]); setScope(null); });
  }, [adminView]);

  // Everything the server filters by. The list, the summary, the count, the header filter lists and
  // the export all use it, so they cannot disagree.
  const params = useCallback((extra = {}) => {
    const p = { scope, ...extra };
    if (appliedSearch) p.search   = canonicalQuery(appliedSearch, SEARCH_FIELDS, SEARCH_FIELD_LABELS);
    const range = localDaysToUtcRange(appliedFrom, appliedTo);
    if (range.from) p.dateFrom = range.from;
    if (range.to)   p.dateTo   = range.to;
    // Relative dates (Today, This month, ...) go to the server as explicit days, from this calendar.
    const f = serializeFiltersForServer(filters);
    if (f) p.filters = f;
    return p;
  }, [scope, appliedSearch, appliedFrom, appliedTo, filters]);

  const sortParams = useCallback(() => (
    sortConfig?.key ? { sortKey: sortConfig.key, sortDir: sortConfig.direction } : {}
  ), [sortConfig]);

  const loadData = useCallback(async (pg = 1, { quiet = false } = {}) => {
    if (!scope) return;
    if (!quiet) setLoading(true);
    try {
      if (view === 'summary') {
        const res = await dataTransferApi.getSummary(params());
        setGroups(res.data?.groups || []);
        setSummaryTruncated(!!res.data?.truncated);
      } else {
        const res = await dataTransferApi.getTransfers(params({ page: pg, pageSize, ...sortParams() }));
        setRows(res.data?.rows || []);
        setTotalCount(res.data?.totalCount || 0);
        setPage(res.data?.page || pg);
      }
    } catch (err) {
      if (!quiet) notify.error(err.response?.data?.error || 'Failed to load data transfers');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [scope, view, params, sortParams, pageSize, notify]);

  useEffect(() => { loadData(1); }, [loadData]);

  // A different list is a different set of rows: the selection does not carry over.
  useEffect(() => { setSelectedId(null); }, [scope, view, page, pageSize, appliedSearch, appliedFrom, appliedTo, filters, sortConfig]);

  const selectedRow = selectedId == null ? null : rows.find(r => r.id === selectedId) || null;
  const regenerateBlock = regenerateBlocker(selectedRow);

  const regenerate = (row) => {
    const target = getRegenerateTarget(row?.exportRecipe);
    if (target) navigate(target.path, { state: regenerateState(target) });
  };

  // Keep the first page live — it is where a transfer in progress shows up.
  useEffect(() => {
    if (view !== 'transfers' || page !== 1) return undefined;
    const id = setInterval(() => loadData(1, { quiet: true }), 15000);
    return () => clearInterval(id);
  }, [view, page, loadData]);

  // The nav item clicked while on this page is a new history entry: start clean.
  const location = useLocation();
  const entryKeyRef = useRef(location.key);
  useEffect(() => {
    if (entryKeyRef.current === location.key) return;
    entryKeyRef.current = location.key;
    setSearchTerm(''); setAppliedSearch('');
    setFilters({}); setSortConfig(null);
    setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  }, [location.key]);

  useEffect(() => {
    const handler = e => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) setShowDateFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const submitSearch = (value) => setAppliedSearch((value ?? '').trim());

  const applyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setShowDateFilter(false);
  };

  const clearDateFilter = () => {
    setDateFrom(''); setDateTo('');
    setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  };

  const openDownload = async () => {
    setPreparingDownload(true);
    try {
      const res = await dataTransferApi.getRows(params({ limit: 20000, ...sortParams() }));
      const data = res.data?.rows || [];
      if (data.length === 0) { notify.warning('Nothing to export for the current filters.'); return; }
      if (res.data?.truncated) {
        notify.warning(`Export capped at ${res.data.returned.toLocaleString()} of ${res.data.totalCount.toLocaleString()} transfers — narrow the filters for the rest.`);
      }
      setDownloadRows(data.map(r => {
        const recipe = parseRecipe(r.exportRecipe);
        return { ...r, when: formatWhen(r.createdAt), fileName: recipe?.fileName || '', parameters: describeParams(recipe) };
      }));
      setShowDownload(true);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to prepare the export');
    } finally {
      setPreparingDownload(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showUser = scope !== 'mine';
  const gridColumns = showUser ? GRID_COLUMNS : GRID_COLUMNS.filter(c => c.key !== 'userName');

  // Header filter values come from the whole filtered set, not this page.
  const optionsProvider = colKey =>
    dataTransferApi.getDistinct(params({ column: colKey })).then(r => r.data);

  if (scopes && scopes.length === 0) {
    return (
      <div className="stui-module-container">
        <div className="stui-module-header"><div className="stui-module-header-left"><h1>Data Transfer History</h1></div></div>
        <div className="stui-td-empty">No transfer history is available to this account.</div>
      </div>
    );
  }

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Data Transfer History</h1>
          {scopes && scopes.length > 1 && (
            <div className="stui-pill-group">
              {scopes.map(s => (
                <button key={s} className={`stui-pill${scope === s ? ' selected' : ''}`} onClick={() => setScope(s)}>
                  {SCOPE_LABELS[s]}
                </button>
              ))}
            </div>
          )}
          <div className="stui-pill-group">
            <button className={`stui-pill${view === 'transfers' ? ' selected' : ''}`} onClick={() => setView('transfers')}>Transfers</button>
            <button className={`stui-pill${view === 'summary' ? ' selected' : ''}`} onClick={() => setView('summary')}>Summary</button>
          </div>
          <span className="stui-module-header-meta">
            {loading ? '' : view === 'summary'
              ? `${groups.length.toLocaleString()} user-day${groups.length !== 1 ? 's' : ''}`
              : `${totalCount.toLocaleString()} transfer${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="stui-module-header-actions">
          <div className="stui-popover-anchor" ref={dateFilterRef}>
            <button
              className={`stui-crud-btn${dateFilterActive ? ' active' : ''}`}
              onClick={() => setShowDateFilter(v => !v)}
              title={dateFilterActive ? `Filtered: ${appliedFrom || '…'} – ${appliedTo || '…'}` : 'Filter by date'}
            ><Calendar size={17} /></button>
            {showDateFilter && (
              <div className="stui-popover">
                <div className="stui-field-group">
                  <label>From</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateFrom} onChange={setDateFrom} />
                </div>
                <div className="stui-field-group">
                  <label>To</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateTo} onChange={setDateTo} />
                </div>
                <div className="stui-popover-footer">
                  {(dateFrom || dateTo || appliedFrom || appliedTo) && (
                    <button className="stui-btn stui-btn-secondary" onClick={clearDateFilter}>Clear</button>
                  )}
                  <button className="stui-btn stui-btn-primary" onClick={applyDateFilter}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {hasColumnFilters(filters) && (
            <button onClick={() => setFilters({})} className="stui-clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{totalCount.toLocaleString()}</span>
            </button>
          )}

          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => { setSearchTerm(''); submitSearch(''); }} />
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search transfers (press Enter)..."
              title={SEARCH_HINT}
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); if (!e.target.value) submitSearch(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitSearch(e.target.value); } }}
            />
          </div>
          <ActionMenu
            items={[
              { key: 'regenerate', icon: <FileDown size={16} />, label: 'Regenerate Report',
                hidden: view !== 'transfers', disabled: !!regenerateBlock,
                title: regenerateBlock || `Re-run ${selectedRow?.name} with its saved parameters and export it again`,
                onClick: () => regenerate(selectedRow) },
              { divider: 'subtle' },
              { key: 'adv-search', icon: <ListFilter size={16} />, label: 'Advanced Search…', onClick: () => setShowAdvSearch(true) },
            ]}
            disabled={preparingDownload}
            onRefresh={() => loadData(page)}
            onDownload={openDownload}
            downloadLabel="Export…"
          />
        </div>
      </div>

      <div className="stui-datasheet-view">
        {view === 'summary' ? (
          <div className="stui-data-grid-container">
            <div className="stui-data-grid-scroll-area">
              <SummaryTable groups={groups} loading={loading || !scope} showUser={showUser} />
            </div>
          </div>
        ) : (
          <FilterableDataGrid
            data={rows.map(toGridRow)}
            columns={gridColumns}
            filters={filters}
            setFilters={setFilters}
            optionsProvider={optionsProvider}
            sortConfig={sortConfig || NO_SORT}
            onSortChange={setSortConfig}
            onRowClick={row => setSelectedId(id => (id === row.id ? null : row.id))}
            onRowDoubleClick={row => { setSelectedId(row.id); if (!regenerateBlocker(row)) regenerate(row); }}
            getRowClassName={row => (row.id === selectedId ? 'stui-row-selected' : '')}
          />
        )}

        {view === 'summary' ? (
          summaryTruncated && (
            <div className="stui-pagination">
              <div className="stui-pagination-info"><span>Showing the most recent 1,000 user-days — narrow the dates or search for the rest.</span></div>
            </div>
          )
        ) : (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={rows.length}
            loading={loading}
            onPage={loadData}
            onPageSize={setPageSize}
          />
        )}
      </div>

      <DownloadModal
        isOpen={showDownload}
        onClose={() => setShowDownload(false)}
        data={downloadRows}
        columns={DOWNLOAD_COLUMNS}
        tableName="AudTrl_DataTransfer"
      />
      <AdvancedSearchModal
        isOpen={showAdvSearch}
        onClose={() => setShowAdvSearch(false)}
        query={searchTerm}
        fields={SEARCH_FIELD_LABELS}
        onApply={q => { setSearchTerm(q); submitSearch(q); }}
      />
    </div>
  );
};

const SummaryTable = ({ groups, loading, showUser }) => {
  const cols = showUser ? 8 : 7;
  return (
    <table className="stui-data-grid">
      <thead>
        <tr>
          <th style={{ width: 170 }}>Day (UTC)</th>
          {showUser && <th style={{ width: 180 }}>User</th>}
          <th style={{ width: 100 }}>Database</th>
          <th style={{ width: 100, textAlign: 'right' }}>Downloads</th>
          <th style={{ width: 90, textAlign: 'right' }}>Uploads</th>
          <th style={{ width: 80, textAlign: 'right' }}>Failed</th>
          <th style={{ width: 100, textAlign: 'right' }}>Rows</th>
          <th>Last</th>
        </tr>
      </thead>
      <tbody>
        {loading && <tr><td colSpan={cols} className="stui-td-empty">Loading...</td></tr>}
        {!loading && groups.length === 0 && <tr><td colSpan={cols} className="stui-td-empty">No transfers for these filters.</td></tr>}
        {!loading && groups.map(g => (
          <tr key={`${g.day}|${g.userId}|${g.databaseSlug}`}>
            <td className="stui-text-nowrap">{formatDateOnly(g.day)}</td>
            {showUser && <td className="stui-truncate" title={g.userName}>{g.userName || g.userId}</td>}
            <td>{g.databaseSlug || '—'}</td>
            <td className="stui-cell-right stui-fw-semibold">{count(g.downloads)}</td>
            <td className="stui-cell-right">{count(g.uploads)}</td>
            <td className="stui-cell-right">{count(g.failed)}</td>
            <td className="stui-cell-right">{count(g.rowsTransferred)}</td>
            <td className="stui-text-nowrap stui-text-sm-muted">{formatWhen(g.lastAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Pagination = ({ page, totalPages, totalCount, pageSize, rowCount, loading, onPage, onPageSize }) => (
  <div className="stui-pagination">
    <div className="stui-pagination-info">
      <span>Showing {rowCount.toLocaleString()} of {totalCount.toLocaleString()} transfers</span>
    </div>
    <div className="stui-pagination-controls">
      <label className="stui-pagination-pagesize">
        <span>Per page:</span>
        <StuiSelect value={pageSize} onChange={e => onPageSize(Number(e.target.value))}>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </StuiSelect>
      </label>
      <div className="stui-pagination-nav">
        <button className="stui-pagination-btn" onClick={() => onPage(1)} disabled={page <= 1 || loading} title="First page"><ChevronFirst size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(page - 1)} disabled={page <= 1 || loading} title="Previous page"><ChevronLeft size={18} /></button>
        <span className="stui-pagination-page-info">Page {page} of {totalPages}</span>
        <button className="stui-pagination-btn" onClick={() => onPage(page + 1)} disabled={page >= totalPages || loading} title="Next page"><ChevronRight size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(totalPages)} disabled={page >= totalPages || loading} title="Last page"><ChevronLast size={18} /></button>
      </div>
    </div>
  </div>
);

export default DataTransferHistoryPage;
